import { useState } from 'react'
import { useGetMany, useGetList, useInput } from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import Autocomplete from '@mui/material/Autocomplete'
import MuiTextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import FormHelperText from '@mui/material/FormHelperText'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import CloseIcon from '@mui/icons-material/Close'

import { validateRequired, validateUnique } from '../../utils/validate'
import {
  COLLECTION_TYPES,
  COLLECTION_TYPES_NAME,
} from '../../components/constants'

const ItemFormDrawer = ({
  open,
  onClose,
  initialValues,
  initialEntity,
  onSubmit,
}) => {
  const [order, setOrder] = useState(initialValues.order)
  const [type, setType] = useState(initialValues.type || '')
  const [entity, setEntity] = useState(initialEntity || null)
  const [searchText, setSearchText] = useState('')
  const [error, setError] = useState('')

  const typeDef = COLLECTION_TYPES.find(({ id }) => id === type)

  const { data: searchResults = [] } = useGetList(
    typeDef?.entity || 'books',
    {
      filter: searchText ? { title: { $iLike: `%${searchText}%` } } : {},
      pagination: { page: 1, perPage: 20 },
      sort: { field: 'title', order: 'ASC' },
    },
    { enabled: open && !!typeDef },
  )

  const handleTypeChange = newType => {
    setType(newType)
    setEntity(null)
    setSearchText('')
  }

  const handleSave = () => {
    if (!type) return setError('Type is required')
    if (!entity) return setError('Please select an item')
    const parsedOrder = Number(order)
    if (order === '' || order === null || Number.isNaN(parsedOrder)) {
      return setError('Order is required')
    }

    setError('')
    onSubmit({ order: parsedOrder, type, entityId: entity.id })
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 380, p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}>
          <Typography variant="h6">
            {initialValues.isNew ? 'Add item' : 'Edit item'}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <MuiTextField
          label="Order"
          type="number"
          variant="standard"
          fullWidth
          sx={{ mb: 2 }}
          value={order}
          onChange={e => setOrder(e.target.value)}
        />

        <MuiTextField
          select
          label="Type"
          variant="standard"
          fullWidth
          sx={{ mb: 2 }}
          value={type}
          onChange={e => handleTypeChange(e.target.value)}>
          {COLLECTION_TYPES.map(({ id, name }) => (
            <MenuItem key={id} value={id}>
              {name}
            </MenuItem>
          ))}
        </MuiTextField>

        {typeDef && (
          <Autocomplete
            options={searchResults}
            getOptionLabel={option => option.title || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={entity}
            onChange={(_, value) => setEntity(value)}
            inputValue={searchText}
            onInputChange={(_, value) => setSearchText(value)}
            filterOptions={x => x}
            renderInput={params => (
              <MuiTextField
                {...params}
                label={typeDef.name}
                variant="standard"
                fullWidth
              />
            )}
          />
        )}

        {error && (
          <FormHelperText error sx={{ mt: 1 }}>
            {error}
          </FormHelperText>
        )}

        <Box
          sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>
  )
}

const CollectionItemsInput = ({ source = 'items' }) => {
  const { setValue, getValues } = useFormContext()
  const items = useWatch({ name: source }) || []

  const {
    fieldState: { error },
  } = useInput({
    source,
    validate: [validateRequired('Items'), validateUnique('order', true)],
    defaultValue: [],
  })

  const [drawer, setDrawer] = useState(null) // { index, isNew } | null

  const bookIds = items.filter(i => i.type === 'book').map(i => i.entityId)
  const courseIds = items.filter(i => i.type === 'course').map(i => i.entityId)

  const { data: books = [] } = useGetMany(
    'books',
    { ids: bookIds },
    { enabled: bookIds.length > 0 },
  )
  const { data: courses = [] } = useGetMany(
    'courses',
    { ids: courseIds },
    { enabled: courseIds.length > 0 },
  )

  const entitiesMap = {}
  books.forEach(b => (entitiesMap[`book-${b.id}`] = b))
  courses.forEach(c => (entitiesMap[`course-${c.id}`] = c))

  const getEntity = item => entitiesMap[`${item.type}-${item.entityId}`]

  const entries = items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => (a.item.order ?? 0) - (b.item.order ?? 0))

  const openAdd = () => {
    const nextOrder =
      items.length > 0 ? Math.max(...items.map(i => i.order || 0)) + 1 : 1
    setDrawer({ index: null, isNew: true, order: nextOrder })
  }

  const openEdit = index => {
    setDrawer({ index, isNew: false, ...items[index] })
  }

  const closeDrawer = () => setDrawer(null)

  const handleSubmit = values => {
    const current = getValues(source) || []
    const duplicate = current.some(
      (it, idx) => idx !== drawer.index && Number(it.order) === values.order,
    )
    if (duplicate) return

    const updated = [...current]
    if (drawer.isNew) {
      updated.push(values)
    } else {
      updated[drawer.index] = values
    }
    setValue(source, updated, { shouldDirty: true, shouldValidate: true })
    setDrawer(null)
  }

  const handleDelete = index => {
    const current = getValues(source) || []
    setValue(
      source,
      current.filter((_, i) => i !== index),
      { shouldDirty: true, shouldValidate: true },
    )
  }

  return (
    <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}>
        <Typography variant="subtitle1" fontWeight={500}>
          Items
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={openAdd}>
          Add item
        </Button>
      </Box>

      {error && (
        <FormHelperText error sx={{ mb: 1 }}>
          {error.message}
        </FormHelperText>
      )}

      <Table size="small" sx={{ border: 1, borderColor: 'divider' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 80 }}>Order</TableCell>
            <TableCell sx={{ width: 120 }}>Type</TableCell>
            <TableCell>Item</TableCell>
            <TableCell sx={{ width: 96 }} align="right">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.length === 0 && (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ py: 2 }}>
                  No items added yet
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {entries.map(({ item, index }) => (
            <TableRow key={index}>
              <TableCell>{item.order}</TableCell>
              <TableCell>
                {COLLECTION_TYPES_NAME[item.type] || item.type}
              </TableCell>
              <TableCell>{getEntity(item)?.title || '—'}</TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => openEdit(index)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {drawer && (
        <ItemFormDrawer
          open
          onClose={closeDrawer}
          initialValues={drawer}
          initialEntity={drawer.isNew ? null : getEntity(drawer) || null}
          onSubmit={handleSubmit}
        />
      )}
    </Box>
  )
}

export default CollectionItemsInput
